import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, UserX, UserCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin({ token, user, onLogout }) {
  usePageTitle('Administração');
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null });

  useEffect(() => {
    fetchUsers();
    fetchAuditLogs();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/audit-logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar logs');
    }
  };

  const toggleAdmin = async (userId, isAdmin) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/admin?is_admin=${!isAdmin}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Status atualizado!');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao atualizar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Usuário excluído!');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
    setDeleteDialog({ open: false, userId: null });
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h1>Administração</h1>
        <p>Gerencie usuários e monitore atividades do sistema</p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="users" data-testid="tab-users">Usuários</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="users" style={{ marginTop: '2rem' }}>
          {loading ? (
            <div>Carregando...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {users.map((u) => (
                <Card key={u.id} data-testid={`user-card-${u.id}`}>
                  <CardContent style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <h3 style={{ fontSize: '1.1rem', fontWeight: 600 }}>{u.nome}</h3>
                          {u.is_admin && <Badge variant="default"><Shield size={12} style={{ marginRight: '0.25rem' }} />Admin</Badge>}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#64748b' }}>{u.email}</div>
                        {u.telefone && <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{u.telefone}</div>}
                        <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                          Cadastrado em: {formatDateTime(u.created_at)}
                        </div>
                      </div>
                      {u.id !== user.id && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleAdmin(u.id, u.is_admin)}
                            data-testid={`toggle-admin-${u.id}`}
                          >
                            {u.is_admin ? <UserX size={16} /> : <UserCheck size={16} />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteDialog({ open: true, userId: u.id })}
                            data-testid={`delete-user-${u.id}`}
                            style={{ color: '#ef4444' }}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="audit" style={{ marginTop: '2rem' }}>
          <Card>
            <CardHeader>
              <CardTitle>Log de Auditoria</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {auditLogs.length === 0 ? (
                  <p style={{ color: '#64748b' }}>Nenhum log encontrado</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {auditLogs.map((log) => (
                      <div key={log.id} style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', borderLeft: '3px solid #0ea5e9' }} data-testid={`audit-log-${log.id}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                          <div>
                            <Badge>{log.action}</Badge>
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>{log.table_name}</span>
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{formatDateTime(log.timestamp)}</div>
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                          Usuário: {log.user_email || 'Sistema'}
                        </div>
                        {log.ip_address && (
                          <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>IP: {log.ip_address}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, userId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita e todos os dados do usuário serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteUser(deleteDialog.userId)} style={{ background: '#ef4444' }}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
